import { useState, useEffect, useRef, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getFrameworks, setFrameworkRegistration, runFrameworksHealthCheck, getFrameworksHealthStatus, getPortals, setPortalRegistration, fmtDate } from "@/lib/api";
import { ExternalLink, Search, Star, Building2, Check, RefreshCw, AlertTriangle, ShieldCheck, LinkIcon, KeyRound, MapPin, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

// Priority portals for CPH's current areas (North East & Scotland) and active notices.
const PRIORITY = ["NEPO (North East Procurement Organisation)", "Scotland Excel", "Efficiency North", "MultiQuote", "eTendersNI / Due North (Northern Ireland)"];

const BROKEN = ["broken", "unreachable", "error"];

const HealthBadge = ({ health }) => {
  if (!health || !health.status) return null;
  if (BROKEN.includes(health.status)) {
    return (
      <span data-testid="fw-health-broken" title={`Returned HTTP ${health.code || "no response"} on last check`} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-red-600 text-white px-2 py-1">
        <AlertTriangle size={11} /> Link may be broken
      </span>
    );
  }
  return (
    <span data-testid="fw-health-ok" title="Link reachable on last check" className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-1">
      <ShieldCheck size={11} /> Link OK
    </span>
  );
};

export default function Frameworks() {
  const [q, setQ] = useState("");
  const [checking, setChecking] = useState(false);
  const pollRef = useRef(null);
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["frameworks"], queryFn: getFrameworks });
  const frameworks = data?.frameworks || [];

  const { data: portalData } = useQuery({ queryKey: ["portals"], queryFn: getPortals });
  const portals = portalData?.portals || [];

  const togglePortal = async (p) => {
    const next = !p.registered;
    qc.setQueryData(["portals"], (old) =>
      old ? { ...old, portals: old.portals.map((x) => (x.name === p.name ? { ...x, registered: next } : x)) } : old
    );
    try {
      await setPortalRegistration(p.name, next);
    } catch {
      qc.invalidateQueries({ queryKey: ["portals"] });
      toast.error("Could not save");
    }
  };

  useEffect(() => () => pollRef.current && clearInterval(pollRef.current), []);

  const checkLinks = useCallback(async () => {
    setChecking(true);
    try {
      await runFrameworksHealthCheck();
      pollRef.current = setInterval(async () => {
        const s = await getFrameworksHealthStatus();
        if (s.status === "done") {
          clearInterval(pollRef.current);
          setChecking(false);
          qc.invalidateQueries({ queryKey: ["frameworks"] });
          toast.success(s.broken > 0 ? `${s.broken} link(s) need attention` : "All framework links are healthy");
        }
      }, 2500);
    } catch {
      setChecking(false);
      toast.error("Couldn't start link check");
    }
  }, [qc]);

  const toggleReg = async (e, f) => {
    e.preventDefault();
    e.stopPropagation();
    const next = !f.registered;
    qc.setQueryData(["frameworks"], (old) =>
      old ? { ...old, frameworks: old.frameworks.map((x) => (x.name === f.name ? { ...x, registered: next } : x)) } : old
    );
    try {
      await setFrameworkRegistration(f.name, next);
    } catch (err) {
      qc.invalidateQueries({ queryKey: ["frameworks"] });
      toast.error("Could not save");
    }
  };

  const filtered = frameworks.filter(
    (f) => f.name.toLowerCase().includes(q.toLowerCase()) || f.note.toLowerCase().includes(q.toLowerCase())
  );
  const priority = filtered.filter((f) => PRIORITY.includes(f.name));
  const rest = filtered.filter((f) => !PRIORITY.includes(f.name));
  const regCount = frameworks.filter((f) => f.registered).length;

  const Card = ({ f, hot }) => (
    <div
      data-testid={`fw-card-${f.name.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
      className={`group block border-2 bg-white p-5 transition-all duration-200 ${
        f.registered ? "border-emerald-600" : hot ? "border-[#2F6DA3]" : "border-zinc-200"
      }`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          {hot && !f.registered && <Star size={14} className="text-[#2F6DA3]" fill="#2F6DA3" strokeWidth={0} />}
          <span className="font-display text-lg font-bold tracking-tight">{f.name}</span>
        </div>
        <button
          data-testid={`fw-reg-${f.name.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
          onClick={(e) => toggleReg(e, f)}
          title={f.registered ? "Registered — click to unmark" : "Mark as registered"}
          className={`shrink-0 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider border-2 px-2 py-1 transition-colors ${
            f.registered ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-zinc-400 border-zinc-300 hover:border-emerald-600 hover:text-emerald-700"
          }`}
        >
          <Check size={12} strokeWidth={3} /> {f.registered ? "Registered" : "Mark registered"}
        </button>
      </div>
      <p className="text-sm text-zinc-600 mb-3">{f.note}</p>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <a
          href={f.url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-[#2F6DA3] hover:underline"
        >
          Register / how to join <ExternalLink size={13} />
        </a>
        <HealthBadge health={f.health} />
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#2F6DA3] mb-1">Get pre-registered · Bid faster</div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter">Portals & Frameworks</h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
            Every framework and e-tendering portal we detect, with a direct link to register. Mark the ones you've signed up for so you can see your coverage at a glance.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            data-testid="check-links-button"
            onClick={checkLinks}
            disabled={checking}
            variant="outline"
            className="border-2 border-[#14304F] font-bold uppercase tracking-wider text-xs"
          >
            {checking ? <RefreshCw size={14} className="mr-2 animate-spin" /> : <LinkIcon size={14} className="mr-2" />}
            {checking ? "Checking…" : "Check links"}
          </Button>
          <div className="border-2 border-emerald-600 px-3 py-2 text-center" data-testid="registered-count">
            <div className="font-display text-2xl font-extrabold tracking-tight text-emerald-700">{regCount}</div>
            <div className="text-[9px] font-mono uppercase tracking-wider text-zinc-500">Registered</div>
          </div>
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              data-testid="framework-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search portals…"
              className="border-2 border-zinc-950 pl-9 pr-4 py-2.5 text-sm w-full md:w-56 focus:outline-none focus:border-[#2F6DA3]"
            />
          </div>
        </div>
      </div>

      {data?.health_broken > 0 && (
        <div data-testid="broken-banner" className="border-2 border-red-500 bg-red-50 p-4 flex items-start gap-3">
          <AlertTriangle size={18} className="text-red-600 shrink-0 mt-0.5" />
          <div className="text-sm text-red-800">
            <strong>{data.health_broken} registration link{data.health_broken === 1 ? "" : "s"} may be broken.</strong> Look for the red badges below — the portal may have moved its page. Last checked {data.health_last_checked ? fmtDate(data.health_last_checked) : "—"}.
          </div>
        </div>
      )}
      {data?.health_last_checked && !data?.health_broken && (
        <div className="text-xs font-mono text-zinc-400 flex items-center gap-1.5" data-testid="health-ok-note">
          <ShieldCheck size={13} className="text-emerald-600" /> All links healthy · last checked {fmtDate(data.health_last_checked)}
        </div>
      )}

      {isLoading ? (
        <div className="text-center py-20 font-mono text-sm text-zinc-400">Loading…</div>
      ) : (
        <>
          {/* Key e-sourcing portals — the register-once systems that hold the documents */}
          <div data-testid="portals-section" className="border-2 border-[#2F6DA3] bg-[#f5f9fc] p-5">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
              <h2 className="font-display text-xl font-bold tracking-tight flex items-center gap-2">
                <KeyRound size={18} className="text-[#2F6DA3]" /> Key e-sourcing portals — register once
              </h2>
              <span className="text-xs font-mono uppercase tracking-wider text-zinc-500">
                {portals.filter((p) => p.registered).length}/{portals.length} registered
              </span>
            </div>
            <p className="text-sm text-zinc-600 mb-3 max-w-3xl">
              The tender documents live behind these portals — you only sign up <strong>once</strong> per portal, then every future contract there is a quick login. Register on these and you'll never hit the "I can't find the docs" wall again. Tick the ones you're already on.
            </p>
            {portalData?.total_live > 0 && (
              <div data-testid="portal-coverage-summary" className="flex items-center gap-3 bg-white border-2 border-[#14304F] px-4 py-2.5 mb-4">
                <span className="font-display text-2xl font-extrabold text-[#2F6DA3]">{portalData.covered_live}</span>
                <span className="text-sm text-zinc-600">
                  of <strong>{portalData.total_live}</strong> live contracts are on portals you're registered for.
                  {portalData.covered_live < portalData.total_live && (
                    <span className="text-zinc-500"> Register on the rest below to unlock the other {portalData.total_live - portalData.covered_live}.</span>
                  )}
                </span>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {portals.map((p) => (
                <div
                  key={p.name}
                  data-testid={`portal-card-${p.name.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                  className={`border-2 bg-white p-4 flex flex-col transition-all duration-200 ${p.registered ? "border-emerald-600" : "border-zinc-200 hover:border-[#2F6DA3]"}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-1.5">
                    <span className="font-display text-base font-bold tracking-tight leading-tight">{p.name}</span>
                    <span className="shrink-0 inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider bg-[#14304F] text-white px-1.5 py-0.5">
                      <MapPin size={9} /> {p.coverage}
                    </span>
                  </div>
                  <p className="text-xs text-zinc-600 mb-3 flex-1">{p.note}</p>
                  {p.live_count > 0 && (
                    <div className="text-[10px] font-mono uppercase tracking-wider text-[#2F6DA3] font-bold mb-2">
                      Unlocks {p.live_count} live contract{p.live_count === 1 ? "" : "s"}
                    </div>
                  )}
                  <div className="flex items-center justify-between gap-2">
                    <a
                      href={p.register_url}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`portal-register-${p.name.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                      className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider bg-[#2F6DA3] text-white px-3 py-1.5 hover:bg-[#234E74] transition-colors"
                    >
                      Register interest <ArrowRight size={13} />
                    </a>
                    <button
                      data-testid={`portal-toggle-${p.name.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                      onClick={() => togglePortal(p)}
                      title={p.registered ? "Registered — click to unmark" : "Mark as registered"}
                      className={`shrink-0 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider border-2 px-2 py-1 transition-colors ${
                        p.registered ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-zinc-400 border-zinc-300 hover:border-emerald-600 hover:text-emerald-700"
                      }`}
                    >
                      <Check size={12} strokeWidth={3} /> {p.registered ? "Done" : "Mark"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {priority.length > 0 && (
            <div data-testid="priority-section">
              <h2 className="font-display text-lg font-bold tracking-tight mb-3 flex items-center gap-2">
                <Star size={16} className="text-[#2F6DA3]" fill="#2F6DA3" strokeWidth={0} /> Priority for your areas
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {priority.map((f) => <Card key={f.name} f={f} hot />)}
              </div>
            </div>
          )}
          <div>
            <h2 className="font-display text-lg font-bold tracking-tight mb-3 flex items-center gap-2 mt-6">
              <Building2 size={16} className="text-zinc-400" /> All portals & frameworks ({rest.length})
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="frameworks-grid">
              {rest.map((f) => <Card key={f.name} f={f} />)}
            </div>
          </div>
          {filtered.length === 0 && <div className="text-center py-12 text-zinc-400 font-mono text-sm">No portals match "{q}"</div>}
        </>
      )}
    </div>
  );
}
